import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { OrgUnitTreeSelectComponent } from '@shared/components/org-unit-tree-select/org-unit-tree-select.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { AttendanceResultGroupedResponse, AttendanceResultRecord } from '../attendance-result/attendance-result.interface';
import { clearAttendanceWeekFilterState, persistAttendanceWeekFilterState, restoreAttendanceWeekFilterState } from '../attendance-week-filter-state.util';
import { AbsenceJustificationDialogComponent } from '../absence/absence-justification-dialog.component';

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
  parentId?: string | null;
  type?: string | null;
  orgPath?: string | null;
  isActive?: boolean;
}

interface CollectionResponse<T> {
  data?: T[];
  items?: T[];
  results?: T[];
  companiesRecords?: T[];
  orgUnits?: T[];
  employees?: T[];
}

interface DelayDay {
  date: Date;
  value: string;
  weekday: string;
  dayLabel: string;
}

interface DelayRow {
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  orgUnitName: string;
  cells: Map<string, AttendanceResultRecord>;
}

interface DelayRowItem {
  day: DelayDay;
  result: AttendanceResultRecord;
}

@Component({
  selector: 'app-delay',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    DateTimePickerFieldComponent,
    LoadingStateComponent,
    OrgUnitTreeSelectComponent,
    CompanySelectComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './delay.component.html',
  styleUrl: './delay.component.scss',
})
export class DelayComponent implements OnInit {
  private static readonly FILTER_STATE_KEY = 'attendance.delays.weekly-filters.v1';
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  results: AttendanceResultRecord[] = [];
  rows: DelayRow[] = [];
  weekDays: DelayDay[] = [];
  isLoading = true;
  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  employeeSearchText: string | EmployeeRecord = '';

  filters = {
    companiesRecordId: '',
    orgUnitId: '',
    employeeId: '',
    weekDate: this.toDateValue(new Date()),
  };

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly companySelection = inject(CompanySelectionService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private employeeSearchHandle: ReturnType<typeof setTimeout> | null = null;
  private delayRequestId = 0;

  ngOnInit(): void {
    this.restoreFilterState();
    this.buildWeek();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildWeek();
      this.buildRows();
    });
    void this.initialize();
  }

  readonly displayEmployee = (value: string | EmployeeRecord | null): string => {
    if (!value) return '';
    return typeof value === 'string' ? value : this.employeeLabel(value);
  };

  get selectedEmployee(): EmployeeRecord | null {
    return this.employees.find((item) => item.employeeId === this.filters.employeeId) || null;
  }

  get canCreateJustification(): boolean {
    return this.authorizationService.hasPermission('attendanceJustifications', 'create');
  }

  async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await Promise.all([this.loadOrgUnits(), this.loadEmployees()]);
      await this.loadDelays();
    } finally {
      this.isLoading = false;
    }
  }

  async applyFilters(): Promise<void> {
    this.currentPage = 1;
    this.persistFilterState();
    this.buildWeek();
    await this.loadDelays();
  }

  async clearFilters(): Promise<void> {
    this.filters = {
      companiesRecordId: this.companies.length === 1 ? this.companySelection.resolveSelection(this.companies) : '',
      orgUnitId: '',
      employeeId: '',
      weekDate: this.toDateValue(new Date()),
    };
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.employeeSearchText = '';
    this.currentPage = 1;
    clearAttendanceWeekFilterState(DelayComponent.FILTER_STATE_KEY);
    this.buildWeek();
    await Promise.all([this.loadOrgUnits(), this.loadEmployees()]);
    await this.loadDelays();
  }

  async refreshTable(): Promise<void> {
    await this.loadDelays();
  }

  async changeWeek(offset: number): Promise<void> {
    const date = this.parseDate(this.filters.weekDate) || new Date();
    date.setDate(date.getDate() + offset * 7);
    this.filters.weekDate = this.toDateValue(date);
    await this.applyFilters();
  }

  async onWeekDateChanged(value: string): Promise<void> {
    if (!value || value === this.filters.weekDate) return;
    this.filters.weekDate = value;
    await this.applyFilters();
  }

  async onCompanyChanged(): Promise<void> {
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.filters.orgUnitId = '';
    this.filters.employeeId = '';
    this.employeeSearchText = '';
    await Promise.all([this.loadOrgUnits(), this.loadEmployees()]);
    await this.applyFilters();
  }

  async onOrgUnitChanged(): Promise<void> {
    this.filters.employeeId = '';
    this.employeeSearchText = '';
    await this.loadEmployees();
    await this.applyFilters();
  }

  onEmployeeInput(value: string | EmployeeRecord): void {
    this.employeeSearchText = value;
    const text = this.displayEmployee(value);
    if (this.selectedEmployee && text !== this.employeeLabel(this.selectedEmployee)) {
      this.filters.employeeId = '';
      void this.applyFilters();
    }
    if (this.employeeSearchHandle) clearTimeout(this.employeeSearchHandle);
    this.employeeSearchHandle = setTimeout(() => void this.loadEmployees(text), 250);
  }

  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const employee = event.option.value as EmployeeRecord;
    this.filters.employeeId = employee.employeeId;
    this.employeeSearchText = employee;
    void this.applyFilters();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.persistFilterState();
    void this.loadDelays();
  }

  private restoreFilterState(): void {
    const state = restoreAttendanceWeekFilterState(DelayComponent.FILTER_STATE_KEY);
    this.filters = {
      companiesRecordId: state.companiesRecordId || '',
      orgUnitId: state.orgUnitId || '',
      employeeId: state.employeeId || '',
      weekDate: state.weekDate || this.filters.weekDate,
    };
    this.employeeSearchText = state.employeeSearchText || '';
    this.currentPage = state.currentPage || 1;
    this.pageSize = state.pageSize || this.pageSize;
  }

  private persistFilterState(): void {
    persistAttendanceWeekFilterState(DelayComponent.FILTER_STATE_KEY, {
      ...this.filters,
      employeeSearchText: this.displayEmployee(this.employeeSearchText),
      currentPage: this.currentPage,
      pageSize: this.pageSize,
    });
  }

  delaysForRow(row: DelayRow): DelayRowItem[] {
    return this.weekDays.flatMap((day) => {
      const result = row.cells.get(day.value);
      return result ? [{ day, result }] : [];
    });
  }

  delayMinutes(result: AttendanceResultRecord): number {
    return Math.max(0, Number(result.delayMinutes || 0));
  }

  totalDelayMinutes(row: DelayRow): number {
    return [...row.cells.values()].reduce((total, result) => total + this.delayMinutes(result), 0);
  }

  hasJustification(result: AttendanceResultRecord): boolean {
    return result.hasJustification === true || result.status === 'justified' || !!result.attendanceJustificationId;
  }

  entryTime(result: AttendanceResultRecord): string {
    if (!result.firstEventAt) return '—';
    const value = new Date(result.firstEventAt);
    if (Number.isNaN(value.getTime())) return '—';
    return new Intl.DateTimeFormat(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: this.locale !== 'en-US',
      ...(result.timezone ? { timeZone: result.timezone } : {}),
    }).format(value);
  }

  async viewResult(result: AttendanceResultRecord): Promise<void> {
    await this.router.navigate(['/attendance/attendance-results', result.attendanceResultId]);
  }

  async createJustification(result: AttendanceResultRecord): Promise<void> {
    if (!this.canCreateJustification || this.hasJustification(result)) return;

    const dialogRef = this.dialog.open(AbsenceJustificationDialogComponent, {
      width: '720px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false,
      restoreFocus: true,
      disableClose: true,
      panelClass: 'delay-justification-dialog-panel',
      data: { result, flow: 'delay' },
    });
    const created = await firstValueFrom(dialogRef.afterClosed());
    if (!created) return;

    result.hasJustification = true;
    result.attendanceJustificationId = created.attendanceJustificationId;
    this.buildRows();
    this.snackBar.open(this.translate.instant('attendanceJustificationsCatalog.notifications.created'), undefined, {
      duration: 3500,
      panelClass: ['snackbar-success'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  private async loadDelays(): Promise<void> {
    const requestId = ++this.delayRequestId;
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceResultGroupedResponse>('attendance-results/grouped', {
          params: {
            hasDelay: 'true',
            isActive: 'true',
            workDateFrom: this.weekDays[0]?.value || '',
            workDateTo: this.weekDays[6]?.value || '',
            page: this.currentPage,
            perPage: this.pageSize,
            groupBy: 'employeeId',
            sort: ['employeeName:asc', 'workDate:asc'],
            ...(this.filters.employeeId ? { employeeId: this.filters.employeeId } : {}),
            ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}),
            ...(this.filters.orgUnitId ? { orgUnitId: this.filters.orgUnitId } : {}),
          },
        })
      );
      if (requestId !== this.delayRequestId) return;
      this.results = (response?.data || [])
        .flatMap((group) => group.items || [])
        .filter((item) => this.delayMinutes(item) > 0 || item.flags?.hasDelay);
      this.totalItems = Number(response?.pagination?.total || 0);
      this.buildRows();
    } catch (error) {
      if (requestId !== this.delayRequestId) return;
      console.error('Error loading delays', error);
      this.results = [];
      this.totalItems = 0;
      this.buildRows();
      this.snackBar.open(
        extractApiErrorMessage(error, this.translate.instant('delaysCatalog.notifications.loadError')),
        undefined,
        { duration: 4000, panelClass: ['snackbar-danger'], horizontalPosition: 'center', verticalPosition: 'bottom' }
      );
    } finally {
      if (requestId === this.delayRequestId) this.isLoading = false;
    }
  }

  private buildRows(): void {
    const rowMap = new Map<string, DelayRow>();
    this.results.forEach((result) => {
      const employee = this.employees.find((item) => item.employeeId === result.employeeId);
      const row = rowMap.get(result.employeeId) || {
        employeeId: result.employeeId,
        employeeNumber: result.employeeNumber || employee?.employeeNumber || '-',
        employeeName: result.employeeName || employee?.fullName || result.employeeNumber || '-',
        orgUnitName: this.orgUnits.find((item) => item.orgUnitId === result.orgUnitId)?.name || employee?.orgUnitName || '-',
        cells: new Map<string, AttendanceResultRecord>(),
      };
      row.cells.set(result.workDate, result);
      rowMap.set(result.employeeId, row);
    });
    this.rows = [...rowMap.values()].sort((left, right) => left.employeeName.localeCompare(right.employeeName));
  }

  private buildWeek(): void {
    const selected = this.parseDate(this.filters.weekDate) || new Date();
    const monday = new Date(selected);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    this.weekDays = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return {
        date,
        value: this.toDateValue(date),
        weekday: new Intl.DateTimeFormat(this.locale, { weekday: 'short' }).format(date),
        dayLabel: new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: 'short' }).format(date),
      };
    });
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CollectionResponse<CompaniesRecord>>('companies-records', { params: { isActive: 'true' } }));
      this.companies = this.extractCollection(response, 'companiesRecords').filter((item) => item.isActive !== false).sort((a, b) => a.name.localeCompare(b.name));
      this.filters.companiesRecordId = this.companySelection.resolveSharedSelection(this.companies, this.filters.companiesRecordId);
    } catch { this.companies = []; }
  }

  private async loadOrgUnits(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | CollectionResponse<OrgUnitRecord>>('org-units'));
      this.orgUnits = this.extractCollection(response, 'orgUnits')
        .filter((item) => item.isActive !== false && (!this.filters.companiesRecordId || item.companiesRecordId === this.filters.companiesRecordId))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch { this.orgUnits = []; }
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selected = this.selectedEmployee;
      const response = await firstValueFrom(this.apiService.get<EmployeeRecord[] | CollectionResponse<EmployeeRecord>>('employees', {
        params: {
          page: 1,
          perPage: 30,
          isActive: 'true',
          ...(search ? { search } : {}),
          ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}),
          ...(this.filters.orgUnitId ? { orgUnitId: this.filters.orgUnitId } : {}),
        },
      }));
      const items = this.extractCollection(response, 'employees').filter((item) => item.isActive !== false).sort((a, b) => a.fullName.localeCompare(b.fullName));
      this.employees = selected && !items.some((item) => item.employeeId === selected.employeeId) ? [selected, ...items] : items;
    } catch { this.employees = []; }
  }

  private extractCollection<T>(response: T[] | CollectionResponse<T>, key: 'companiesRecords' | 'orgUnits' | 'employees'): T[] {
    if (Array.isArray(response)) return response;
    return response[key] || response.data || response.items || response.results || [];
  }

  private employeeLabel(employee: EmployeeRecord): string {
    return `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`;
  }

  private get locale(): string {
    return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
  }

  private parseDate(value: string): Date | null {
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
  }

  private toDateValue(date: Date): string {
    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
  }
}
